import AgentsCountMetric from "./agents-count-metric";
import AgentsStateChart from "./agents-state-chart";
import HighestAgentCapacityMetric from "./highest-agent-capacity-metric";
import InteractionMetricsSummary from "./interaction-metrics-summary";
import InteractionsCountMetric from "./interactions-counts-metric";
import InteractionsStateChart from "./interactions-state-chart";
import MeanAgentCapacityMetric from "./mean-agent-capacity-metric";
import MetricBox from "components-for/display/metric-box";
import QueueLengthChart from "./queue-length-chart";
import RecentlyOfflineAgentsMetric from "./recently-offline-agents-metric";
import { AGENTS_STATE_CHART_ENABLED } from "constants/feature-flags";
import { AgentStateType, InteractionMetric } from "@edgetier/watchtower-types";
import { faStopwatch, faUserCheck, faUserFriends, faUserTimes } from "@fortawesome/pro-duotone-svg-icons";
import { FunctionComponent, memo, useState } from "react";
import { InteractionState } from "@edgetier/watchtower-types";
import { IProps } from "./live-interaction-metrics.types";
import { LIVE_POLL_INTERVAL_MILLISECONDS } from "constants/pages/live";
import { roundDownToInterval } from "utilities/round-down-to-interval";
import { subMinutes } from "date-fns";
import { useInterval } from "react-use";
import "./live-interaction-metrics.scss";
import {
    getNonBlockedPollParameters,
    useBlockedInteractionsCount,
    useNonBlockedInteractionsCounts,
} from "./live-interaction-metrics.utilities";
import {
    faClipboardListCheck,
    faClock,
    faCommentDots,
    faComments,
    faCommentTimes,
    faHandshake,
} from "@fortawesome/pro-duotone-svg-icons";

/**
 * Show live interaction data (queuing and interactions being handled).
 * @returns Live metrics, charts, and tables.
 */
const LiveInteractionMetrics: FunctionComponent<IProps> = () => {
    // Move the count time window on every poll.
    const [nonBlockedPollParameters, setNonBlockedPollParameters] = useState(getNonBlockedPollParameters());
    useInterval(() => {
        setNonBlockedPollParameters(getNonBlockedPollParameters());
    }, LIVE_POLL_INTERVAL_MILLISECONDS);

    const { data: nonBlockedInteractionCounts, isLoading: isNonBlockedLoading } =
        useNonBlockedInteractionsCounts(nonBlockedPollParameters);

    // Blocked interactions can't be filtered by date and are really slow to count, so it's done separately.
    const { data: blockedCount, isLoading: isBlockedLoading } = useBlockedInteractionsCount();

    return (
        <>
            <div className="live__columns live__interactions-count-metrics">
                <div className="live__metrics">
                    <MetricBox
                        icon={faComments}
                        isLoading={isBlockedLoading || isNonBlockedLoading}
                        title="Total Conversations Open"
                        tooltipContent="Queuing and ongoing interactions"
                        value={(nonBlockedInteractionCounts?.total ?? 0) + (blockedCount ?? 0)}
                    />

                    <MetricBox
                        icon={faCommentDots}
                        isLoading={isNonBlockedLoading}
                        title="Active"
                        tooltipContent="Interactions currently being handled"
                        value={nonBlockedInteractionCounts?.handlingCount ?? 0}
                    />

                    <InteractionsCountMetric
                        icon={faCommentTimes}
                        queryParameters={{ interactionState: [InteractionState.Blocked] }}
                        title="Snoozed Conversations"
                        tooltipContent="Interactions currently being postponed"
                    />

                    <MetricBox
                        icon={faUserFriends}
                        isLoading={isNonBlockedLoading}
                        title="Queuing"
                        tooltipContent="Customers waiting in the queue"
                        value={nonBlockedInteractionCounts?.queuingCount ?? 0}
                    />

                    <InteractionMetricsSummary
                        getDynamicParameters={getNonBlockedPollParameters}
                        queryParameters={{
                            interactionState: [InteractionState.Handling],
                        }}
                        getValue={({ maximumHandlingTimeSeconds }) => maximumHandlingTimeSeconds}
                        icon={faStopwatch}
                        interactionMetric={InteractionMetric.HandlingTime}
                        title="Max Duration"
                        tooltipContent="Maximum duration of ongoing interactions"
                    />

                    <InteractionMetricsSummary
                        getDynamicParameters={getNonBlockedPollParameters}
                        queryParameters={{
                            interactionState: [InteractionState.Queuing],
                        }}
                        getValue={({ meanQueueTimeSeconds }) => meanQueueTimeSeconds}
                        icon={faClock}
                        interactionMetric={InteractionMetric.HandlingTime}
                        title="Average Queue Time"
                        tooltipContent="Average wait time of queuing customers"
                    />

                    <InteractionMetricsSummary
                        getDynamicParameters={getNonBlockedPollParameters}
                        queryParameters={{
                            interactionState: [InteractionState.Queuing],
                        }}
                        getValue={({ maximumQueueTimeSeconds }) => maximumQueueTimeSeconds}
                        icon={faStopwatch}
                        interactionMetric={InteractionMetric.HandlingTime}
                        title="Max Queue Time"
                        tooltipContent="Maximum wait time of all queuing customers"
                    />
                    <InteractionMetricsSummary
                        getDynamicParameters={() => ({
                            endDateTime: new Date(),
                            startDateTime: roundDownToInterval(
                                subMinutes(new Date(), 30),
                                LIVE_POLL_INTERVAL_MILLISECONDS
                            ),
                        })}
                        getValue={({ serviceLevelAgreementPercentage }) =>
                            typeof serviceLevelAgreementPercentage === "number"
                                ? serviceLevelAgreementPercentage * 100
                                : 0
                        }
                        icon={faHandshake}
                        interactionMetric={InteractionMetric.ServiceLevelAgreement}
                        pollIntervalMilliseconds={LIVE_POLL_INTERVAL_MILLISECONDS * 4}
                        title="Service Level Agreement"
                        tooltipContent="Interactions answered within SLA in the last 30 minutes"
                    />
                </div>

                <QueueLengthChart />
            </div>

            <div className="live__metrics">
                <MetricBox
                    icon={faClipboardListCheck}
                    isLoading={false}
                    title="Scheduled Agents"
                    tooltipContent="Agents scheduled to be online"
                    value={null}
                />

                <AgentsCountMetric
                    icon={faUserCheck}
                    queryParameters={{ agentStateType: AgentStateType.Available }}
                    title="Available Agents"
                    tooltipContent="Agents ready to handle interactions"
                />

                <AgentsCountMetric
                    icon={faUserTimes}
                    queryParameters={{ agentStateType: AgentStateType.Unavailable }}
                    title="Unavailable Agents"
                    tooltipContent="Agents online but not available to handle interactions"
                />

                <RecentlyOfflineAgentsMetric />

                <HighestAgentCapacityMetric />
                <MeanAgentCapacityMetric />
            </div>

            <div className="live__charts">
                <div>{AGENTS_STATE_CHART_ENABLED && <AgentsStateChart />}</div>
                <div>
                    <InteractionsStateChart
                        blockedCount={blockedCount ?? 0}
                        deflectingCount={nonBlockedInteractionCounts?.deflectingCount ?? 0}
                        handlingCount={nonBlockedInteractionCounts?.handlingCount ?? 0}
                        isLoading={isBlockedLoading || isNonBlockedLoading}
                        queuingCount={nonBlockedInteractionCounts?.queuingCount ?? 0}
                        wrapUpCount={nonBlockedInteractionCounts?.wrapUpCount ?? 0}
                    />
                </div>
            </div>
        </>
    );
};

export default memo(LiveInteractionMetrics);
